<script setup lang="ts">
import VsText from '@/components/vision/VsText.vue'
import { LineItem } from '@/models/line-item'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import { computed, ref } from 'vue'
import { useLineItemsTotal } from '@/components/jobs/line-items/useLineItemsTotal'
import IconContractOff from '@/components/vision/icons/icon-contract-off.vue'
import IconSignatureOff from '@/components/vision/icons/icon-signature-off.vue'
import { formatDateWithYear } from '@/utils/date-helpers'
import { useStore } from 'vuex-composition-helpers'

interface Props {
  lineItem: LineItem
  showRates: boolean
  isCosted: boolean
  isSigned?: boolean | undefined
}

const props = withDefaults(defineProps<Props>(), {
  isSigned: undefined,
})

const store = useStore()

const formatter = useCurrencyFormatter()
const rateFormatter = useCurrencyFormatter(5)

function getLineItemCaptionText(quantity: number | null, rate: number | null) {
  if (props.showRates && (props.isCosted || (rate && quantity))) {
    // parseFloat eliminates trailing zeros
    return `${parseFloat(quantity?.toFixed(5) ?? '0')} × ${rateFormatter.format(rate ?? 0)}`
  }

  if (quantity !== null && (!props.showRates || !props.isCosted)) {
    return [`× ${quantity}`, props.lineItem.unitOfMeasure].filter(Boolean).join(' ')
  }

  return ''
}

// users.all is keyed by uuid, which is what workerId holds.
const workerName = computed(() => {
  const workerId = props.lineItem.details.workerId
  return workerId ? (store.state.users.all[workerId]?.name as string | undefined) : undefined
})

const workDate = computed(() => {
  const { workDate } = props.lineItem.details
  return workDate ? formatDateWithYear(workDate) : undefined
})

const hasDetails = computed(() => {
  return Boolean(workerName.value || props.lineItem.details.serialNumber || workDate.value)
})

const lineItemAmount = computed(() => {
  if (!props.showRates || !props.isCosted) return
  return formatter.format(useLineItemsTotal(ref([props.lineItem])).value)
})
</script>

<template>
  <div class="line-item" :data-test="`line-item-row-${lineItem.id}`">
    <div class="line-item-content">
      <vs-text
        class="line-item-name"
        over-flow-wrap="anywhere"
        :data-test="`line-item-name-${lineItem.id}`"
        >{{ lineItem.name }}
      </vs-text>

      <div
        v-if="hasDetails"
        class="line-item-details"
        :data-test="`line-item-details-${lineItem.id}`"
      >
        <span v-if="workerName" class="line-item-detail">{{ workerName }}</span>
        <span v-if="lineItem.details.serialNumber" class="line-item-detail">{{
          lineItem.details.serialNumber
        }}</span>
        <span v-if="workDate" class="line-item-detail">{{ workDate }}</span>
      </div>

      <span class="line-item-detail" :data-test="`line-item-caption-${lineItem.id}`">
        {{ getLineItemCaptionText(lineItem.quantity, lineItem.rate) }}
      </span>
    </div>

    <div class="line-item-total">
      <div
        v-if="lineItemAmount"
        class="line-item-amount"
        :data-test="`line-item-amount-${lineItem.id}`"
      >
        {{ lineItemAmount }}
      </div>

      <div class="indicators">
        <div
          v-if="showRates"
          :class="['rate-agreement', lineItem.matchesRateAgreement && 'is-matched']"
          :data-test="`line-item-rate-agreement-${lineItem.id}`"
        >
          <v-tooltip v-if="lineItem.matchesRateAgreement" top max-width="380">
            <template #activator="{ on }">
              <span class="v-icon material-symbols-outlined" v-on="on">contract</span>
            </template>
            <span>Matches rate agreement</span>
          </v-tooltip>
          <v-tooltip v-else top max-width="380">
            <template #activator="{ attrs, on }">
              <span v-bind="attrs" v-on="on">
                <icon-contract-off />
              </span>
            </template>
            <span>Does not match rate agreement</span>
          </v-tooltip>
        </div>
        <div
          v-if="isSigned !== undefined"
          :class="['sign-off', isSigned ? 'is-signed' : '']"
          :data-test="`line-item-signoff-${lineItem.id}`"
        >
          <v-tooltip v-if="isSigned" top>
            <template #activator="{ on }">
              <span class="v-icon material-symbols-outlined" v-on="on">signature</span>
            </template>
            <span>Signed off</span>
          </v-tooltip>
          <v-tooltip v-else top>
            <template #activator="{ on }">
              <span v-on="on">
                <icon-signature-off />
              </span>
            </template>
            <span>Not signed off</span>
          </v-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-item {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  font-family: var(--font-regular);
}

.line-item-content,
.line-item-total {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.line-item-name {
  text-box: trim-both cap alphabetic;
}

.line-item-details {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  overflow-wrap: anywhere;
}

.line-item-detail {
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
  text-box: trim-both cap alphabetic;
  letter-spacing: 0.01em;
}

.line-item-total {
  align-items: flex-end;
}

.line-item-amount {
  text-box: trim-both cap alphabetic;
}

.indicators {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
}

.rate-agreement {
  display: flex;
  width: 16px;
  height: 16px;
  color: var(--color-grey--lighter);

  span:has(> svg) {
    display: inline-flex;
  }

  &.is-matched {
    color: var(--color-green--dark);
  }
}

.sign-off {
  display: flex;
  width: 16px;
  height: 16px;
  color: var(--color-grey--lighter);

  span:has(> svg) {
    display: inline-flex;
  }

  &.is-signed {
    color: var(--color-green--dark);
  }
}

span.material-symbols-outlined {
  font-size: var(--space-base);
}

.line-item-amount {
  justify-content: flex-end;
  white-space: nowrap;
}

.line-item-details {
  min-width: 0;
}
</style>
